import { ambientWorldCatalog, parseAmbientWorldEventKind } from './fishing-world-catalog'

export interface FishingWorldAudioParameter {
  setValueAtTime: (value: number, startTime: number) => void
  exponentialRampToValueAtTime: (value: number, endTime: number) => void
}

export interface FishingWorldAudioGain {
  gain: FishingWorldAudioParameter
  connect: (destination: FishingWorldAudioDestination) => void
}

export interface FishingWorldAudioOscillator {
  type: OscillatorType
  frequency: FishingWorldAudioParameter
  connect: (destination: FishingWorldAudioGain) => void
  start: (when?: number) => void
  stop: (when?: number) => void
}

export type FishingWorldAudioDestination = object

export interface FishingWorldAudioContext {
  currentTime: number
  destination: FishingWorldAudioDestination
  createGain: () => FishingWorldAudioGain
  createOscillator: () => FishingWorldAudioOscillator
}

export type FishingWorldEventAudio = Readonly<{
  enable: () => void
  play: (value: unknown) => boolean
}>

export type FishingWorldEventAudioOptions = Readonly<{
  reducedMotion?: () => boolean
  createContext?: () => FishingWorldAudioContext | null
}>

export type FishingWorldAudioPlaybackHistory = Readonly<{
  claim: (eventId: string) => boolean
}>

const PLAYED_EVENT_HISTORY_LIMIT = 128

/** Keeps a bounded session-only record so event-array updates cannot replay sound. */
export function createFishingWorldAudioPlaybackHistory(): FishingWorldAudioPlaybackHistory {
  const played = new Set<string>()
  const order: string[] = []

  return {
    claim(eventId) {
      if (played.has(eventId)) return false
      played.add(eventId)
      order.push(eventId)
      if (order.length > PLAYED_EVENT_HISTORY_LIMIT) {
        const oldest = order.shift()
        if (oldest) played.delete(oldest)
      }
      return true
    },
  }
}

function browserAudioContext(): FishingWorldAudioContext | null {
  if (typeof window === 'undefined' || !window.AudioContext) return null
  try {
    const context = new window.AudioContext()
    const nativeGains = new WeakMap<FishingWorldAudioGain, GainNode>()
    return {
      get currentTime() { return context.currentTime },
      destination: {},
      createGain() {
        const nativeGain = context.createGain()
        const gain: FishingWorldAudioGain = {
          gain: nativeGain.gain,
          connect() { nativeGain.connect(context.destination) },
        }
        nativeGains.set(gain, nativeGain)
        return gain
      },
      createOscillator() {
        const nativeOscillator = context.createOscillator()
        return {
          get type() { return nativeOscillator.type },
          set type(value) { nativeOscillator.type = value },
          frequency: nativeOscillator.frequency,
          connect(gain) {
            const nativeGain = nativeGains.get(gain)
            if (nativeGain) nativeOscillator.connect(nativeGain)
          },
          start(when) { nativeOscillator.start(when) },
          stop(when) { nativeOscillator.stop(when) },
        }
      },
    }
  } catch {
    return null
  }
}

/**
 * A capability boundary for optional ambient event sounds. It never initializes
 * audio before an explicit user action and intentionally has no network assets.
 */
export function createFishingWorldEventAudio(options: FishingWorldEventAudioOptions = {}): FishingWorldEventAudio {
  let enabled = false
  let context: FishingWorldAudioContext | null = null

  return {
    enable() {
      enabled = true
    },
    play(value) {
      const kind = parseAmbientWorldEventKind(value)
      if (!enabled || !kind || options.reducedMotion?.() || ambientWorldCatalog[kind].audio !== 'optional') return false
      try {
        context ??= options.createContext?.() ?? browserAudioContext()
        if (!context) return false
        const now = context.currentTime
        const oscillator = context.createOscillator()
        const gain = context.createGain()
        oscillator.type = 'sine'
        oscillator.frequency.setValueAtTime(420, now)
        oscillator.frequency.exponentialRampToValueAtTime(260, now + 0.18)
        gain.gain.setValueAtTime(0.0001, now)
        gain.gain.exponentialRampToValueAtTime(0.025, now + 0.02)
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.2)
        oscillator.connect(gain)
        gain.connect(context.destination)
        oscillator.start(now)
        oscillator.stop(now + 0.22)
        return true
      } catch {
        return false
      }
    },
  }
}
